import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Tooltip,
  Typography,
  IconButton,
  Divider,
  CardActions,
  Button,
  Fab,
  useMediaQuery,
} from '@material-ui/core';
import SpellcheckIcon from '@material-ui/icons/Spellcheck';
import CheckIcon from '@material-ui/icons/Check';
import EditIcon from '@material-ui/icons/Edit';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    display: 'flex',
    margin: '4px 0 0 0',
    padding: '0 4px',
    height: 35,
    minHeight: 35,
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.texts.generalTitleBackgrounds}`,
    justifyContent: 'flex-end',
  },

  tooltipText: {
    textTransform: 'uppercase',
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.09rem',
  },

  tooltip_text: {
    letterSpacing: '0.09rem',
    fontSize: theme.fonts.responsiveMedium,
  },

  editIcon: {
    height: '20px',
    width: '20px',
    minWidth: '20px',
    minHeight: '20px',
    borderRadius: '50%',
    margin: '2px',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  actionButton: {
    color: 'white',
    minWidth: '80px',
    height: '18px',
    minHeight: '18px',
    margin: '4px',
    fontSize: theme.fonts.responsiveSmall,
    backgroundColor: theme.palette.accents.dark,
    '&:hover': {
      background: `${theme.palette.accents.main} !important`,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  icon_button: {
    height: '12px',
    width: '12px',
    minHeight: '12px',
    minWidth: '12px',
  },

  icon_fab: {
    height: '14px',
    width: '14px',
    minHeight: '14px',
    minWidth: '14px',
  },

  fab_icon_container: {
    height: '20px',
    width: '20px',
    minWidth: '20px',
    minHeight: '20px',
    margin: '2px 4px',
    color: 'white',
    backgroundColor: theme.palette.accents.dark,
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },
}));

export const SummaryCardsActions = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles(theme);
  const {
    gui: { examinationListDrawer },
  } = useSelector((state) => state);
  const smallScreen = useMediaQuery('(max-width: 1400px)') || examinationListDrawer;

  const {
    isConfirmed,
    isRelabeled,
    annotatorRole,
    moduleStatus,
    handleEditButtonClick,
    onConfirmClick,
    module,
    extraModule,
    multipleModules,
    parentModuleName,
    hasResults,
  } = props;
  const renderTooltipTypography = (text, bold) => (
    <Typography
      style={{ fontWeight: bold ? 'bold' : 'normal' }}
      className={classes.tooltipText}
      color='inherit'>
      {text}
    </Typography>
  );

  if (!hasResults) {
    return <CardActions className={classes.container} />;
  }
  // Case 1: No annotator role
  else if (!annotatorRole) {
    return (
      <CardActions className={classes.container}>
        <div>
          <Tooltip
            title={
              <Typography className={classes.tooltip_text} color='inherit'>
                {t('ResultView.no_annotator')}
              </Typography>
            }
            arrow
            placement='left-end'>
            <span>
              <IconButton size='small' className={classes.editIcon} disabled>
                <EditIcon fontSize='small' className={classes.icon_button} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </CardActions>
    );
  }

  // Case 2: Already confirmed or relabeled
  if (
    isConfirmed ||
    isRelabeled ||
    (multipleModules && multipleModules?.every((module) => module.isReviewed))
  ) {
    return (
      <CardActions className={classes.container}>
        <Tooltip
          arrow
          placement='left-end'
          title={
            <Typography className={classes.tooltip_text} color='inherit'>
              {t('ResultView.tooltip_revise')} {t(`ResultView.${parentModuleName || module}`)}
            </Typography>
          }>
          <Fab
            size='small'
            color='secondary'
            aria-label='add'
            className={classes.fab_icon_container}
            onClick={handleEditButtonClick}>
            <EditIcon fontSize='small' className={classes.icon_button} />
          </Fab>
        </Tooltip>
      </CardActions>
    );
  }

  // Case 3: Default (show relabel + confirm buttons)
  return (
    <CardActions
      className={classes.container}
      style={{
        justifyContent: 'flex-end',
      }}>
      <div>
        {/* Handle multiple modules (for Diagnostics) */}
        {multipleModules ? (
          <>
            {/* Render confirm buttons for each module */}
            {multipleModules.map(
              (moduleConfig, index) =>
                !moduleConfig.isReviewed && (
                  <Tooltip
                    key={moduleConfig.name}
                    title={
                      <>
                        {renderTooltipTypography(moduleConfig.moduleStatus.currentApplied, true)}
                        {annotatorRole && (
                          <>
                            <Divider style={{ margin: '3% 0' }} />
                            {renderTooltipTypography(moduleConfig.moduleStatus.tooltipAction)}
                          </>
                        )}
                      </>
                    }
                    arrow
                    placement='top-start'>
                    <span>
                      {smallScreen ? (
                        <Fab
                          size='small'
                          color='secondary'
                          disabled={!annotatorRole}
                          aria-label='add'
                          className={classes.fab_icon_container}
                          onClick={moduleConfig.onConfirmClick}>
                          <>
                            {moduleConfig.moduleStatus.fabIcon}
                            <Typography
                              style={{
                                position: 'absolute',
                                top: '0',
                                left: '20%',
                                fontSize: '8px',
                              }}
                              color='inherit'>
                              {t(`ResultView.${moduleConfig.name}`).charAt(0)}
                            </Typography>
                          </>
                        </Fab>
                      ) : (
                        <Button
                          size='small'
                          variant='contained'
                          disabled={!annotatorRole}
                          onClick={moduleConfig.onConfirmClick}
                          color='primary'
                          // startIcon={moduleConfig.moduleStatus.fabIcon}
                          className={classes.actionButton}>
                          {moduleConfig.moduleStatus.buttonText} {t(`ResultView.${moduleConfig.name}`)}
                        </Button>
                      )}
                    </span>
                  </Tooltip>
                )
            )}

            {/* Revise button for parent module */}
            <Tooltip
              title={renderTooltipTypography(
                `${t('ResultView.relable')} ${t(`ResultView.${parentModuleName}`)}`,
                true
              )}
              arrow
              placement='top-end'>
              <span>
                {smallScreen ? (
                  <Fab
                    size='small'
                    color='secondary'
                    aria-label='add'
                    className={classes.fab_icon_container}
                    onClick={handleEditButtonClick}>
                    <EditIcon fontSize='small' className={classes.icon_button} />
                  </Fab>
                ) : (
                  <Button
                    size='small'
                    variant='contained'
                    onClick={handleEditButtonClick}
                    color='primary'
                    // startIcon={<EditIcon fontSize='small' className={classes.icon_button} />}
                    className={classes.actionButton}>
                    {t('ResultView.relable')}
                  </Button>
                )}
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            {/* Original single/dual module logic */}
            {extraModule && (
              <>
                <Tooltip
                  title={
                    <>
                      {renderTooltipTypography(moduleStatus?.currentApplied, true)}
                      {annotatorRole && (
                        <>
                          <Divider style={{ margin: '3% 0' }} />
                          {renderTooltipTypography(moduleStatus?.tooltipAction)}
                        </>
                      )}
                    </>
                  }
                  arrow
                  placement='top-start'>
                  <span>
                    {smallScreen ? (
                      <Fab
                        size='small'
                        color='secondary'
                        disabled={!annotatorRole}
                        aria-label='add'
                        className={classes.fab_icon_container}
                        onClick={moduleConfig.onConfirmClick}>
                        {moduleStatus?.fabIcon}
                      </Fab>
                    ) : (
                      <Button
                        size='small'
                        variant='contained'
                        disabled={!annotatorRole}
                        onClick={moduleConfig.onConfirmClick}
                        color='primary'
                        // startIcon={moduleStatus?.fabIcon}
                        className={classes.actionButton}>
                        {moduleStatus?.buttonText} {extraModule ? `${t(`ResultView.${extraModule}`)}` : ''}
                      </Button>
                    )}
                  </span>
                </Tooltip>
              </>
            )}

            <Tooltip
              title={
                <>
                  {renderTooltipTypography(moduleStatus.currentApplied, true)}
                  {annotatorRole && (
                    <>
                      <Divider style={{ margin: '3% 0' }} />
                      {renderTooltipTypography(moduleStatus.tooltipAction)}
                    </>
                  )}
                </>
              }
              arrow
              placement='top-start'>
              <span>
                {smallScreen ? (
                  <Fab
                    size='small'
                    color='secondary'
                    disabled={!annotatorRole}
                    aria-label='add'
                    className={classes.fab_icon_container}
                    onClick={onConfirmClick}>
                    {moduleStatus.fabIcon}
                  </Fab>
                ) : (
                  <Button
                    size='small'
                    variant='contained'
                    disabled={!annotatorRole}
                    onClick={onConfirmClick}
                    color='primary'
                    // startIcon={moduleStatus.fabIcon}
                    className={classes.actionButton}>
                    {moduleStatus.buttonText} {extraModule ? `${t(`ResultView.${module}`)}` : ''}
                  </Button>
                )}
              </span>
            </Tooltip>
            <Tooltip
              title={renderTooltipTypography(
                `${t('ResultView.relable')} ${t(`ResultView.${module}`)} ${
                  extraModule ? `& ${t(`ResultView.${extraModule}`)}` : ''
                }`,
                true
              )}
              arrow
              placement='top-end'>
              <span>
                {smallScreen ? (
                  <Fab
                    size='small'
                    color='secondary'
                    aria-label='add'
                    className={classes.fab_icon_container}
                    onClick={handleEditButtonClick}>
                    <EditIcon fontSize='small' className={classes.icon_button} />
                  </Fab>
                ) : (
                  <Button
                    size='small'
                    variant='contained'
                    onClick={handleEditButtonClick}
                    color='primary'
                    // startIcon={<EditIcon fontSize='small' className={classes.icon_button} />}
                    className={classes.actionButton}>
                    {t('ResultView.relable')}
                  </Button>
                )}
              </span>
            </Tooltip>
          </>
        )}
      </div>
    </CardActions>
  );
};
